import { useEffect } from 'react';
import { BlogPost } from '../pages/Blog/blogPostsData';

const BASE_SITE_URL = 'https://www.thewealthyminer.com';

const getAbsoluteUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `${BASE_SITE_URL}${url}`;
};

interface BlogMetaTagsProps {
  post: BlogPost;
}

const BlogMetaTags: React.FC<BlogMetaTagsProps> = ({ post }) => {
  useEffect(() => {
    const blogUrl = `${BASE_SITE_URL}${post.link}`;
    const imageUrl = getAbsoluteUrl(post.image);

    // Update or create Open Graph meta tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update or create Twitter meta tags
    const updateTwitterMetaTag = (name: string, content: string) => {
      let metaTag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update or create regular meta tags
    const updateRegularMetaTag = (name: string, content: string) => {
      let metaTag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Keep title as "The Wealthy Miner" - don't change it
    // document.title = `${post.title} | The Wealthy Miner`;

    // Update description
    updateRegularMetaTag('description', post.description);

    // Update canonical URL
    let canonicalTag = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', blogUrl);

    // Open Graph tags (LinkedIn primarily uses these)
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:url', blogUrl);
    updateMetaTag('og:title', post.title);
    updateMetaTag('og:description', post.description);
    updateMetaTag('og:image', imageUrl);
    updateMetaTag('og:image:secure_url', imageUrl); // LinkedIn prefers secure URLs
    updateMetaTag('og:image:width', '1200');
    updateMetaTag('og:image:height', '630');
    updateMetaTag('og:image:alt', post.title);
    updateMetaTag('og:site_name', 'The Wealthy Miner');
    updateMetaTag('og:locale', 'en_US'); // LinkedIn uses this

    // Twitter Card tags
    updateTwitterMetaTag('twitter:card', 'summary_large_image');
    updateTwitterMetaTag('twitter:url', blogUrl);
    updateTwitterMetaTag('twitter:title', post.title);
    updateTwitterMetaTag('twitter:description', post.description);
    updateTwitterMetaTag('twitter:image', imageUrl);
    updateTwitterMetaTag('twitter:image:alt', post.title);

    // Article specific tags
    updateMetaTag('article:author', post.author);
    if (post.date) {
      // Parse date format like "27 Nov 2025" or "21 Jan 2026"
      try {
        const dateObj = new Date(post.date);
        if (!isNaN(dateObj.getTime())) {
          updateMetaTag('article:published_time', dateObj.toISOString());
        }
      } catch (e) {
        // If date parsing fails, skip the published_time tag
      }
    }
  }, [post]);

  return null;
};

export default BlogMetaTags;
